import { IonRouterLink } from "@ionic/react";
import { AnimatePresence } from "framer-motion";
import { Fragment, useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import TertiaryButton from "@components/buttons/TertiaryButton";
import ClientAvatar from "@components/ClientAvatar";
import DateStamp from "@components/dates/DateStamp";
import DocumentPopover from "@components/drive/DocumentPopover";
import styles from "@components/drive/Drive.module.css";
import FolderPopover from "@components/drive/FolderPopover";
import ExpertAvatar from "@components/ExpertAvatar";
import Table, { TableHeader } from "@components/Table";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading2 from "@components/typography/Heading2";
import useFileResourceDownloader from "@hooks/useFileResourceDownloader";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import { FileResourceFragment, GetJourneyWithDocumentsQuery, SortDirectionEnum } from "@typing/Generated";
import { getFileImage } from "@utils/fileUtils";
import { imageUrl } from "@utils/imageUtils";
import {
  getFileResourceTitle,
  getFolderTitle,
  sortByStringFieldCaseInsensitive,
  sortByTimestamp
} from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Journey = GetJourneyWithDocumentsQuery["journey"];

type Props = {
  handleFolderCreate: () => void;
  journey: Journey;
};

enum SortBy {
  NAME = "name",
  UPDATED_AT = "updated_at"
}

type FileResourceProps = {
  fileResource: FileResourceFragment;
  handleDownload: (fileResource: FileResourceFragment) => void;
  index?: number;
  journey: Journey;
};

const FileResource = ({ fileResource, handleDownload, index, journey }: FileResourceProps) => {
  const intl = useIntl();

  return (
    <Fragment>
      <tr className={index === 0 ? styles.noRadius : ""}>
        <td className={styles.documentsTableFileTitle}>
          <ButtonLink
            className={fileResource.attachment?.scanning ? styles.disabledDoc : ""}
            onClick={() => handleDownload(fileResource)}
            stylized={false}
          >
            <div className={styles.tableFileTitle}>
              <img
                aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.file" })}
                className={styles.tableFileImg}
                src={imageUrl(getFileImage(fileResource))}
              />
              <div className={styles.tableFileTitleString}>
                <BodyCopy color="gray-500">{getFileResourceTitle(fileResource, intl)}</BodyCopy>
              </div>
            </div>
          </ButtonLink>
        </td>
        <td className={styles.documentsTableFileFrom}>
          {fileResource.creator.__typename === "Expert" ? (
            <ExpertAvatar expert={fileResource.creator} size={30} />
          ) : (
            <ClientAvatar
              clientId={fileResource.creator.id}
              color="primary-500"
              firstName={fileResource.creator.firstName}
              journeyId={journey.id}
              lastName={fileResource.creator.lastName}
              size={30}
            />
          )}
        </td>
        <td>
          <div className={styles.tableFlex}>
            <div className={styles.hideMobile}>
              <BodyCopy color="primary">
                <DateStamp value={fileResource.updatedAt} />
              </BodyCopy>
            </div>

            <DocumentPopover fileResource={fileResource} journey={journey} />
          </div>
        </td>
      </tr>
      <tr className={styles.spacing}>
        <td />
      </tr>
    </Fragment>
  );
};

const Documents = ({ handleFolderCreate, journey }: Props) => {
  const intl = useIntl();
  const [sortBy, setSortBy] = useState<SortBy>(SortBy.UPDATED_AT);
  const myExpert = useMyExpert();
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.descending);
  const expandedDocumentsOpener = useOpener();
  const fileResourceDownloader = useFileResourceDownloader();

  const documentTableHeaders: TableHeader<SortBy>[] = [
    { columnSortBy: SortBy.NAME },
    { messageKey: "pages.drive.tableHeaders.from" },
    { columnSortBy: SortBy.UPDATED_AT, hideMobile: true }
  ];

  const sortedFiles = useMemo(() => {
    let documents: FileResourceFragment[] = [...journey.fileResources].filter(resource => !resource.worksheet);

    if (sortBy === SortBy.NAME) {
      documents = sortByStringFieldCaseInsensitive(documents, d =>
        d.title ? d.title : intl.formatMessage({ id: `models.pdfType.${d.pdfType}` })
      );
    }
    if (sortBy === SortBy.UPDATED_AT) {
      documents = sortByTimestamp(documents, documents => documents.updatedAt);
    }

    if (sortDirection === SortDirectionEnum.ascending) {
      return documents.reverse();
    }
    return documents;
  }, [intl, journey.fileResources, sortBy, sortDirection]);

  const handleDownload = useCallback(
    (fr: FileResourceFragment) => {
      fileResourceDownloader.handleDownload(fr, journey.id);
    },
    [fileResourceDownloader, journey.id]
  );

  return (
    <div className={styles.documents}>
      <Heading2>
        <FormattedMessage id="pages.drive.documents" />
      </Heading2>
      <div className={styles.folders}>
        {journey.folders.map(folder => (
          // eslint-disable-next-line react/forbid-elements
          <IonRouterLink
            key={folder.id}
            routerLink={journeyRoute({
              journeyId: journey.id,
              linkAfterJourney: `/drive/folders/${folder.id}`,
              myExpert
            })}
          >
            <div className={styles.folder}>
              <div className={styles.folderIconAndText}>
                <Icon color="white" icon="folder" />
                <BodyCopy bold color="white">
                  {getFolderTitle(folder, intl)}
                </BodyCopy>
              </div>
              <FolderPopover folder={folder} journey={journey} />
            </div>
          </IonRouterLink>
        ))}
        <ButtonLink onClick={handleFolderCreate} stylized={false}>
          <div className={styles.createFolder}>
            <Icon color="primary" icon="add" />
            <BodyCopy bold color="primary">
              <FormattedMessage id="pages.drive.createFolder" />
            </BodyCopy>
          </div>
        </ButtonLink>
      </div>

      {sortedFiles.length > 0 && (
        <Table<SortBy>
          gradientTable
          headerKey="pages.drive.tableHeaders"
          headers={documentTableHeaders}
          setSortBy={setSortBy}
          setSortDirection={setSortDirection}
          skipScroll
          skipSpacing
          sortBy={sortBy}
          sortDirection={sortDirection}
        >
          {sortedFiles.slice(0, 4).map((fileResource, index) => (
            <FileResource
              fileResource={fileResource}
              handleDownload={handleDownload}
              index={index}
              journey={journey}
              key={fileResource.id}
            />
          ))}
          <AnimatePresence>
            {expandedDocumentsOpener.isOpen &&
              sortedFiles
                .slice(4)
                .map(fileResource => (
                  <FileResource
                    fileResource={fileResource}
                    handleDownload={handleDownload}
                    journey={journey}
                    key={fileResource.id}
                  />
                ))}
          </AnimatePresence>
        </Table>
      )}

      {sortedFiles.length > 4 && (
        <div className={styles.displayMore}>
          <TertiaryButton
            icon={expandedDocumentsOpener.isOpen ? "chevronUp" : "chevronDown"}
            iconPosition="end"
            onClick={expandedDocumentsOpener.toggle}
            textKey={expandedDocumentsOpener.isOpen ? "pages.drive.displayLess" : "pages.drive.displayMore"}
          />
        </div>
      )}
    </div>
  );
};

export default Documents;
